// Entrega de un pedido RETIRA (R8) — se renderiza DENTRO del detalle del pedido
// cuando el encargado abre un pedido despachado con método RETIRA.
//
// Flujo: si faltan las etiquetas, las imprime acá mismo → escanea cada bulto
// (todos, obligatorio) → confirma → el pedido pasa a ENTREGADO (se mueve a
// pedidos_entregados y sale del tablero) vía entregarPedidoDespachado.

import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, hasRol } from "../../../context/AuthContext";
import FacturadoBadge from "./FacturadoBadge";
import { useApp } from "../../../context/AppContext";
import QrScanner from "../../../components/QrScanner";
import { entregarPedidoDespachado } from "../services/pedidosFS";
import { unidadesDeItem } from "../services/etiquetasFS";

export default function EntregaRetiraDetalle({ pedido }) {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { toast, haptics } = useApp();

  // Unidades reales del pedido: bultos (accesorios) + paquetes (perfiles).
  const unidades = useMemo(() => unidadesDeItem({ pedidoId: pedido.id, bultos: pedido.bultos, paquetes: pedido.paquetes }), [pedido.id, pedido.bultos, pedido.paquetes]);
  const totalUnidades = unidades.length || 1;
  const [escaneados, setEscaneados] = useState(new Set()); // set de KEYS
  const [error, setError]           = useState("");
  const [saving, setSaving]         = useState(false);

  const handleScan = useCallback((raw) => {
    setError("");
    const texto = (typeof raw === "string" ? raw : raw?.text || raw?.decodedText || String(raw)).trim();
    const u = unidades.find(x => x.key === texto);
    if (!u) {
      setError(texto.startsWith(pedido.id) ? "Etiqueta que no corresponde a esta entrega" : "Etiqueta de otro pedido");
      return;
    }
    setEscaneados(prev => {
      if (prev.has(u.key)) { setError(`${u.label} ya escaneado`); return prev; }
      return new Set([...prev, u.key]);
    });
  }, [pedido.id, unidades]);

  const todos = escaneados.size === totalUnidades;

  const esAdmin = hasRol(profile, "admin");

  async function confirmar() {
    // Candado: no se puede entregar si no está facturado (salvo admin, para pruebas).
    if (pedido.facturado !== true && !esAdmin) {
      toast.error("Falta facturar este pedido. No se puede entregar hasta que Administración lo facture.");
      return;
    }
    setSaving(true);
    try {
      await entregarPedidoDespachado(pedido.id, {
        uid: user?.uid || null,
        nombre: profile?.nombre || profile?.displayName || null,
        bultosEscaneados: [...escaneados],
      });
      haptics?.success?.();
      toast.success("Pedido entregado ✓");
      navigate("/pedidos");
    } catch (e) {
      console.error("[EntregaRetiraDetalle] error:", e);
      haptics?.error?.();
      toast.error(e?.message || "No se pudo registrar la entrega");
      setSaving(false);
    }
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "100px" }}>

      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 16px 14px" }}>
        <button
          onClick={() => navigate("/pedidos")}
          style={{ background: "none", border: "none", color: "#64748b", fontSize: "0.85rem", cursor: "pointer", padding: 0, marginBottom: "10px", display: "flex", alignItems: "center", gap: "4px" }}
        >
          ← Volver
        </button>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
          <div>
            <p style={{ margin: 0, fontSize: "0.7rem", fontWeight: 700, color: "#94a3b8", letterSpacing: "0.07em", textTransform: "uppercase" }}>
              Entrega · Retira · #{pedido.numero || pedido.id}
            </p>
            <h1 style={{ margin: "3px 0 0", fontSize: "1.3rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
              {pedido.cliente || "—"}
            </h1>
          </div>
          <span style={{ flexShrink: 0, background: "#fef3c7", color: "#92400e", fontSize: "0.68rem", fontWeight: 700, padding: "4px 10px", borderRadius: "999px", letterSpacing: "0.05em", marginTop: "4px" }}>
            PENDIENTE DE ENTREGA
          </span>
        </div>
      </div>

      <div style={{ padding: "16px" }}>

        {/* Estado de facturación + aviso si falta facturar */}
        <div style={{
          display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px",
          padding: "10px 12px", borderRadius: "12px",
          background: pedido.facturado === true ? "#f0fdf4" : "#fef2f2",
          border: `1.5px solid ${pedido.facturado === true ? "#86efac" : "#fca5a5"}`,
        }}>
          <FacturadoBadge facturado={pedido.facturado} size="lg" />
          <span style={{ fontSize: "0.8rem", color: pedido.facturado === true ? "#166534" : "#b91c1c", fontWeight: 600 }}>
            {pedido.facturado === true
              ? "Listo para entregar."
              : (esAdmin ? "Falta facturar (admin puede entregar igual)." : "No se puede entregar hasta facturar.")}
          </span>
        </div>

        {/* Las etiquetas se imprimen en el control (encargado). Acá se escanean
            para verificar la entrega: bultos (accesorios) + paquetes (perfiles). */}
        <>
            <p style={{ margin: "0 0 10px", fontSize: "0.7rem", fontWeight: 700, color: "#64748b", letterSpacing: "0.07em", textTransform: "uppercase" }}>
              Escaneá las {totalUnidades} unidad{totalUnidades !== 1 ? "es" : ""}
            </p>

            {/* Progreso (bultos + paquetes) */}
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
              {unidades.map(u => {
                const ok = escaneados.has(u.key);
                const esPaq = u.tipo === "paquete";
                return (
                  <div key={u.key} title={u.label} style={{
                    minWidth: "40px", height: "40px", padding: "0 6px", borderRadius: "10px",
                    background: ok ? "#16a34a" : esPaq ? "#eff6ff" : "#f1f5f9",
                    color: ok ? "#fff" : esPaq ? "#1d4ed8" : "#94a3b8",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: "14px",
                    border: `1.5px solid ${ok ? "#16a34a" : esPaq ? "#bfdbfe" : "#e2e8f0"}`,
                  }}>{ok ? "✓" : (esPaq ? "📏" : "📦") + u.indice}</div>
                );
              })}
            </div>
            <p style={{ margin: "0 0 12px", fontSize: "0.85rem", color: "#64748b" }}>
              {escaneados.size}/{totalUnidades} escaneadas
              {" "}({Number(pedido.bultos) || 0} bultos · {Number(pedido.paquetes) || 0} paquetes)
            </p>

            {error && (
              <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: "10px", padding: "10px 14px", color: "#dc2626", fontSize: "0.83rem", fontWeight: 600, marginBottom: "12px" }}>
                ⚠️ {error}
              </div>
            )}

            {!todos ? (
              <QrScanner onScanSuccess={handleScan} qrbox={{ width: 240, height: 240 }} />
            ) : (
              <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: "14px", padding: "18px", textAlign: "center" }}>
                <div style={{ fontSize: "32px", marginBottom: "6px" }}>✅</div>
                <p style={{ margin: 0, fontWeight: 700, color: "#166534", fontSize: "1rem" }}>
                  Todas las unidades escaneadas
                </p>
              </div>
            )}
        </>
      </div>

      {/* Botón fijo: confirmar entrega */}
      {(
        <div className="cta-fija" style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          padding: "12px 16px 20px", background: "#fff",
          borderTop: "1px solid #e2e8f0", boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
        }}>
          {!todos && (
            <p style={{ margin: "0 0 8px", fontSize: "0.78rem", color: "#b45309", textAlign: "center" }}>
              Escaneá todos los bultos para confirmar
            </p>
          )}
          <button
            onClick={confirmar}
            disabled={!todos || saving}
            style={{
              width: "100%", padding: "16px", borderRadius: "14px", border: "none",
              fontWeight: 700, fontSize: "1.05rem",
              background: (todos && !saving) ? "#0f172a" : "#e2e8f0",
              color: (todos && !saving) ? "#fff" : "#94a3b8",
              cursor: (todos && !saving) ? "pointer" : "not-allowed",
            }}
          >
            {saving ? "Registrando…" : "✅ Confirmar entrega"}
          </button>
        </div>
      )}
    </div>
  );
}
